import { Controller } from "@hotwired/stimulus";

// polar.sh or a subdomain over HTTPS. A substring check ("polar.sh") also accepted origins like
// https://polar.sh.attacker.example.
const isPolarOrigin = (origin: string): boolean => {
  try {
    const { protocol, hostname } = new URL(origin);
    return protocol === "https:" && (hostname === "polar.sh" || hostname.endsWith(".polar.sh"));
  } catch {
    return false;
  }
};

export default class extends Controller {
  static values = {
    checkoutUrl: String,
    clientSecret: String,
  };

  declare checkoutUrlValue: string;
  declare clientSecretValue: string;
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private iframe: HTMLIFrameElement | null = null;

  connect() {
    this.loadCheckout();
  }

  loadCheckout() {
    if (!this.checkoutUrlValue) {
      this.showError("Checkout configuration error. Please try again.");
      return;
    }

    // Za inline checkout, Polar koristi iframe
    // Checkout URL sa embed=true za embed mode
    const embedUrl = `${this.checkoutUrlValue}?embed=true`;

    this.iframe = document.createElement("iframe");
    this.iframe.src = embedUrl;
    this.iframe.style.cssText = "width: 100%; height: 700px; border: none;";
    this.iframe.allow = "payment";

    // Clear loading state and add iframe
    this.element.innerHTML = "";
    this.element.appendChild(this.iframe);

    // Listen for messages from Polar checkout
    this.messageHandler = this.handleMessage.bind(this);
    window.addEventListener("message", this.messageHandler);
  }

  handleMessage(event: MessageEvent) {
    // Only trust messages from our own checkout iframe, served by Polar
    if (!this.iframe || event.source !== this.iframe.contentWindow) return;
    if (!isPolarOrigin(event.origin)) return;

    const { type, data } = event.data || {};

    switch (type) {
      case "checkout.completed":
        window.location.href = "/subscription/success";
        break;
      case "checkout.closed":
        window.location.href = "/subscription/new";
        break;
      case "checkout.error":
        console.error("Polar checkout error:", data);
        this.showError("Payment error occurred. Please try again.");
        break;
      case "checkout.resize":
        // Polar šalje resize event sa potrebnom visinom
        if (this.iframe && data?.height) {
          this.iframe.style.height = `${data.height}px`;
        }
        break;
    }
  }

  showError(message: string) {
    this.element.innerHTML = `
      <div class="flex flex-col items-center justify-center py-16 text-center">
        <svg class="w-12 h-12 text-error mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clip-rule="evenodd" />
        </svg>
        <p class="text-error font-medium mb-4">${message}</p>
        <a href="/subscription/new" class="btn btn-primary">Go Back</a>
      </div>
    `;
  }

  disconnect() {
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
  }
}
